import {
    Box,
    capitalize,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../../data";
import {useT} from "../../../i18n";
import DataTable from "../../data/DataTable";
import {useGetApi} from "../../../client";
import useCustomFilters from "../../data/useCustomFilters";
import SearchField from "../../SearchField";

export type ZugewieseneAnbauteilKostenFilter = {
    fahrzeugId: string;
    anbauteilId: string;
    search?: string;
    filtered?: string;
};

export type ZugewieseneAnbauteilKosten = {
    id: string;
    kostenVersion: number;
    kostenStunde: number;
};

export type Props = Omit<
    DataRequest<ZugewieseneAnbauteilKostenFilter>,
    "filter"
> &
    Partial<Pick<DataRequest<ZugewieseneAnbauteilKostenFilter>, "filter">> & {
    zugewiesenesAnbauteilId: string;
    anbauteilId: string;
};

const tableName: string = "fahrzeug-anbauteil-kostenhistorie"

export default function ZugewieseneAnbauteilKostenDataTable({
                                                                anbauteilId,
                                                                zugewiesenesAnbauteilId,
                                                                ...input
                                                            }: Props) {
    const {id: fahrzeugId = ""} = useParams<{
        id: string;
    }>();
    const {t: translation} = useT("fuhrpark");
    const request = useDataRequest<ZugewieseneAnbauteilKostenFilter>({
        filter: {fahrzeugId: fahrzeugId, anbauteilId: anbauteilId},
        ...input,
    });
    const [filteredState, setFilteredState] = useState("");

    return (
        <>
            <Paper elevation={1} sx={{padding: 3}}>
                <Stack direction={"column"} spacing={2}>
                    <Typography variant="h5">{capitalize(translation("cost-history"))}</Typography>
                    <FilterComp
                        request={request}
                        filteredStateFilter={filteredState}
                    />
                    <AnbauteilResults request={request} setFilteredState={setFilteredState}/>
                </Stack>
            </Paper>
        </>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<ZugewieseneAnbauteilKostenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<ZugewieseneAnbauteilKostenFilter>({
        tableName: tableName,
        defaultFilter: {
            fahrzeugId: request.filter.fahrzeugId,
            anbauteilId: request.filter.anbauteilId,
            search: "",
        }
    });
    useEffect(() => {
        setFilter({
            fahrzeugId: currentCustomFilter.fahrzeugId,
            anbauteilId: currentCustomFilter.anbauteilId,
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <SearchField
                value={currentCustomFilter.search}
                onChange={(val) => setCustomField("search", val)}
            />
        </Box>
    );
}

function AnbauteilResults({
                              request,
                              setFilteredState,
                          }: {
    request: DataRequestState<ZugewieseneAnbauteilKostenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<ZugewieseneAnbauteilKostenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const getApi = useGetApi();


    const ladeZugewieseneAnbauteilKosten: DataLoader<
        ZugewieseneAnbauteilKostenFilter,
        ZugewieseneAnbauteilKosten
    > = useCallback(
        async (params) =>
            (await getApi()).fahrzeuge.getFahrzeugAnbauteilKosten({...params}),
        [getApi]
    );

    return (
        <>
            <DataTable
                columns={useColumns()}
                request={request}
                queryKey={[tableName]}
                loadData={ladeZugewieseneAnbauteilKosten}
                name={tableName}
                setFiltered={setFilteredState}
            />
        </>
    );
}

function useColumns(): Array<GridColDef<ZugewieseneAnbauteilKosten>> {
    const {t: translation} = useT("fuhrpark");

    return useMemo(
        () => [
            {
                field: "gueltigVon",
                headerName: capitalize(translation("assign-attachment-part-valid-from")),
                flex: 1,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    }
                    return "";
                },
            },
            {
                field: "gueltigBis",
                headerName: capitalize(translation("assign-attachment-part-valid-to")),
                flex: 1,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    }
                    return "aktuell";
                },
            },
            {
                field: "kostenStunde",
                headerName: capitalize(translation("assign-attachment-part-cost-hour")),
                flex: 1,
                headerAlign: "right",
                align: "right",
            },
        ],
        [translation]
    );
}
